import { useEffect, useState } from "react";
import Avatar from "react-avatar";
import { Col, Container, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  useLazyGetNotificationsQuery,
  useLazyGetUnreadNotificationsCountQuery,
} from "../../../redux/api/notification/notificationAPI";
import {
  setPrivileges,
  setToken,
} from "../../../redux/features/auth/auth-slice";
import { selectProfile, clearProfile } from "../../../redux/features/user/user-slice";
import { getConsumerPrivateRoutes } from "../../../screens/routes";
import { useTheme } from "../../hooks/useTheme";
import oversightLogoIconDark from "../../oversight-core/assets/images/logo-icon-dark.svg";
import oversightLogoIcon from "../../oversight-core/assets/images/logo-icon.svg";
import oversightLogoDark from "../../oversight-core/assets/images/logo-sidebar-dark.svg";
import oversightLogo from "../../oversight-core/assets/images/logo-sidebar.svg";
import { AppRoute } from "../../oversight-core/interfaces/app-routes";
import BottomNavBar from "../../oversight-core/shared-components/bottom-nav-bar/bottom-nav-bar";
import LogoutModal from "../../oversight-core/shared-components/logout-modal/logout-modal";
import NotificationsPanel from "../../oversight-core/shared-components/notifications-panel/notifications-panel";
import ProfileMenu from "../../oversight-core/shared-components/profile-menu/profile-menu";
import SideBar from "../../oversight-core/shared-components/sidebar/side-bar";
import MaterialIcon from "../../oversight-core/ui-elements/material-icon/material-icon";
import ScrollToTop from "../../oversight-core/ui-elements/scroll-to-top/ScrollToTop";
import ErrorBoundary from "../../oversight-core/utils/ErrorBoundary";
import styles from "./dashboard-layout.module.scss";

const DashboardLayout = () => {
  const theme = useTheme();
  const profile = useSelector(selectProfile);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentLogo = theme === "dark" ? oversightLogoDark : oversightLogo;
  const currentLogoIcon =
    theme === "dark" ? oversightLogoIconDark : oversightLogoIcon;
  const currentLogos = [currentLogo, currentLogoIcon];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const { pathname } = location;
  const [pageTitle, setPageTitle] = useState<string | undefined>();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const [triggerGetNotifications] = useLazyGetNotificationsQuery();

  const [triggerGetUnreadNotificationsCount] =
    useLazyGetUnreadNotificationsCountQuery();

  useEffect(() => {
    triggerGetNotifications({
      pageSize: 10,
      pageNumber: 0,
      isFirstTime: true,
      ascending: true,
    });
  }, [triggerGetNotifications]);

  useEffect(() => {
    triggerGetUnreadNotificationsCount();
  }, [triggerGetUnreadNotificationsCount]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (
        !target.closest("#notifications-panel") &&
        !target.closest("#notifications-icon")
      ) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotifications]);

  const getCurrentPageInfo = (path: string) => {
    const subRoutes = getConsumerPrivateRoutes(profile?.role).find(
      (route) => route.root,
    )?.subRoutes;

    return subRoutes
      ?.filter((pi) => matchPath({ path: pi.path, end: true }, path))
      .sort((a, b) => b.path.length - a.path.length)[0];
  };

  const handleCollapse = () => {
    setCollapsed(!collapsed);
  };

  useEffect(() => {
    const animateTitleLength = 2;
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;
        if (nextIndex === animateTitleLength - 1) clearInterval(interval);
        return nextIndex % animateTitleLength;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const logout = () => {
    localStorage.clear();
    dispatch(setToken(""));
    dispatch(setPrivileges([]));
    dispatch(clearProfile());
    setShowLogoutModal(false);
    navigate(AppRoute.LOGIN);
  };

  const locationPageTitle = (
    location.state as { pageTitle?: string } | null
  )?.pageTitle?.trim();
  const pageName =
    pageTitle?.trim() ||
    locationPageTitle ||
    getCurrentPageInfo(pathname)?.name?.trim();

  return (
    <>
      <div className={`${styles["main-container"]} vh-100`}>
        <BottomNavBar />
        <div
          className={`${styles["navbar-container"]} d-none d-md-block ${
            collapsed ? styles["collapsed"] : styles["notCollapsed"]
          }`}
        >
          <SideBar
            collapsed={collapsed}
            routes={getConsumerPrivateRoutes(profile?.role)}
            logos={currentLogos}
          />
        </div>
        <div className={styles["main-content"]}>
          <div className={`ps-lg-2 ${styles["header-container"]}`}>
            <Container fluid className={styles["header"]}>
              <Row className="justify-content-between align-items-center">
                <Col xs="auto">
                  <Row className="align-items-center">
                    <Col xs="auto" className="d-flex align-items-center pe-0">
                      <MaterialIcon
                        icon="menu"
                        onClick={handleCollapse}
                        className={`cursor-pointer me-2 d-none d-md-flex ${styles["collapse-icon"]}`}
                        color="var(--primary)"
                        size={22}
                      />
                    </Col>
                    <Col xs="auto" className="pe-4 pe-lg-0">
                      <Row className="align-items-center gap-2">
                        {pageName === "Home" ? (
                          <>
                            <Col
                              xs="auto"
                              className={`${styles["title-long"]} p-0 d-none d-md-block`}
                            >
                              <div className={styles[`heading-text-container`]}>
                                <ul
                                  className={`${
                                    styles[`heading-text-list`]
                                  } m-0 p-0`}
                                  style={{
                                    top: `-${currentIndex * 50}px`,
                                  }}
                                >
                                  <li className={styles[`heading-text`]}>
                                    <span
                                      className={styles[`heading-text-color`]}
                                    >
                                      Hello {profile?.firstName},
                                    </span>{" "}
                                    Welcome to Oversight
                                  </li>
                                  <li className={styles[`heading-text`]}>
                                    Home
                                  </li>
                                </ul>
                              </div>
                            </Col>
                            <Col
                              xs="auto"
                              className={`${styles["title"]} p-0 d-md-none`}
                            >
                              Home
                            </Col>
                          </>
                        ) : (
                          <Col xs="auto" className={`${styles["title"]} p-0`}>
                            <Row className="align-items-center g-2">
                              <Col className="col-auto pe-0 d-none d-md-inline-block">
                                {pageName}
                              </Col>
                              <Col className="col-auto pe-0 d-md-none">
                                {pageName}
                              </Col>
                            </Row>
                          </Col>
                        )}
                      </Row>
                    </Col>
                  </Row>
                </Col>
                <Col className="col-auto col-xl-7 col-xxl-6">
                  <Row className="align-items-center justify-content-end">
                    <Col
                      xs="auto"
                      className="pe-lg-4 me-lg-3 order-first order-md-last"
                    >
                      <Row className="align-items-center">
                        {/* Notification icon hidden */}
                        <Col
                          xs="auto"
                          className="px-2 order-1 order-lg-2 position-relative"
                        >
                          <Avatar
                            name={`${profile?.firstName || ""} ${
                              profile?.lastName || ""
                            }`.trim()}
                            size="40"
                            round={true}
                            fgColor="var(--avatar-text-color)"
                            onClick={() => {
                              setShowProfileMenu(!showProfileMenu);
                              setShowNotifications(false);
                            }}
                            className={`${styles.customAvatar} cursor-pointer`}
                          />
                        </Col>
                        <Col
                          xs="auto"
                          className="p-0 d-none d-lg-block order-lg-3"
                        >
                          <button
                            type="button"
                            className={`${styles.profileNameButton} text-dark font-size-14 font-weight-500 position-relative`}
                            aria-expanded={showProfileMenu}
                            aria-haspopup="true"
                            onClick={() => {
                              setShowProfileMenu(!showProfileMenu);
                              setShowNotifications(false);
                            }}
                          >
                            <div>{profile?.firstName}</div>
                            {profile?.lastName &&
                              profile?.lastName !== profile?.firstName && (
                                <div>{profile?.lastName}</div>
                              )}
                          </button>
                        </Col>
                        <ProfileMenu
                          show={showProfileMenu}
                          onClose={() => setShowProfileMenu(false)}
                          onLogout={() => setShowLogoutModal(true)}
                        />
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Container>
          </div>
          <div
            className={`dashboard-scrollable-parent ps-lg-2 pb-4 mt-2 mt-xl-0 ${styles["content-container"]}`}
          >
            <Container fluid>
              <ScrollToTop />
              <Row>
                <Col xs={12} className={styles["content"]}>
                  <ErrorBoundary key={pathname}>
                    <div key={pathname} className="page-transition-wrapper">
                      <Outlet context={{ setPageTitle }} />
                    </div>
                  </ErrorBoundary>
                </Col>
              </Row>
            </Container>
          </div>
          <NotificationsPanel showNotifications={showNotifications} />
        </div>
      </div>
      <LogoutModal
        show={showLogoutModal}
        onCancel={() => setShowLogoutModal(false)}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={logout}
      />
    </>
  );
};

export default DashboardLayout;
